/**
 * Voice Library Store
 *
 * Manages voice cloning state for ElevenLabs integration
 */

import { proxy } from "valtio";

// =============================================================================
// Types
// =============================================================================

export interface Voice {
  voice_id: string;
  name: string;
  category: "premade" | "cloned" | "generated";
  description?: string;
  labels?: Record<string, string>;
  preview_url?: string;
  available_for_tiers?: string[];
  settings?: {
    stability: number;
    similarity_boost: number;
    style?: number;
    use_speaker_boost?: boolean;
  };
}

export interface VoiceCloneRequest {
  name: string;
  description?: string;
  audioFiles: File[];
  labels?: Record<string, string>;
}

// =============================================================================
// Store
// =============================================================================

interface VoiceStore {
  voices: Voice[];
  loading: boolean;
  error: string | null;
  cloneProgress: number | null;
  ttsGenerating: boolean;
}

export const voiceStore = proxy<VoiceStore>({
  voices: [],
  loading: false,
  error: null,
  cloneProgress: null,
  ttsGenerating: false,
});

// =============================================================================
// API Base URL
// =============================================================================

function getApiBaseUrl(): string {
  // In production, use the deployed worker
  // In development, use local or staging
  return (
    import.meta.env.VITE_AI_GATEWAY_URL ||
    "https://ai-gateway-proxy.vizimix.workers.dev"
  );
}

// =============================================================================
// Actions
// =============================================================================

/**
 * Fetch all voices from ElevenLabs
 */
export async function fetchVoices(): Promise<void> {
  voiceStore.loading = true;
  voiceStore.error = null;

  try {
    const response = await fetch(`${getApiBaseUrl()}/v1/voices`, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch voices: ${response.statusText}`);
    }

    const data = await response.json();
    voiceStore.voices = data.voices || [];
  } catch (e) {
    voiceStore.error =
      e instanceof Error ? e.message : "Failed to fetch voices";
    throw e;
  } finally {
    voiceStore.loading = false;
  }
}

/**
 * Clone a voice from audio samples
 */
export async function cloneVoice(request: VoiceCloneRequest): Promise<Voice> {
  voiceStore.cloneProgress = 0;
  voiceStore.error = null;

  try {
    const formData = new FormData();
    formData.append("name", request.name);
    if (request.description) {
      formData.append("description", request.description);
    }
    if (request.labels) {
      formData.append("labels", JSON.stringify(request.labels));
    }

    // Add audio files
    for (const file of request.audioFiles) {
      formData.append("files", file);
    }

    voiceStore.cloneProgress = 30;

    const response = await fetch(`${getApiBaseUrl()}/v1/voices/clone`, {
      method: "POST",
      body: formData,
    });

    voiceStore.cloneProgress = 70;

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || "Failed to clone voice");
    }

    const clonedVoice = await response.json();
    voiceStore.cloneProgress = 100;

    // Add to local store
    voiceStore.voices.push(clonedVoice);

    return clonedVoice;
  } catch (e) {
    voiceStore.error = e instanceof Error ? e.message : "Failed to clone voice";
    throw e;
  } finally {
    voiceStore.cloneProgress = null;
  }
}

/**
 * Delete a cloned voice
 */
export async function deleteVoice(voiceId: string): Promise<void> {
  voiceStore.error = null;

  try {
    const response = await fetch(`${getApiBaseUrl()}/v1/voices/${voiceId}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error(`Failed to delete voice: ${response.statusText}`);
    }

    // Remove from local store
    voiceStore.voices = voiceStore.voices.filter((v) => v.voice_id !== voiceId);
  } catch (e) {
    voiceStore.error =
      e instanceof Error ? e.message : "Failed to delete voice";
    throw e;
  }
}

/**
 * Generate speech from text using a voice
 */
export async function generateSpeech(
  text: string,
  voiceId: string,
  options?: {
    stability?: number;
    similarity_boost?: number;
    style?: number;
  },
): Promise<Blob> {
  voiceStore.ttsGenerating = true;
  voiceStore.error = null;

  try {
    const response = await fetch(
      `${getApiBaseUrl()}/v1/text-to-speech/${voiceId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          text,
          model_id: "eleven_multilingual_v2",
          voice_settings: {
            stability: options?.stability ?? 0.5,
            similarity_boost: options?.similarity_boost ?? 0.75,
            style: options?.style ?? 0,
          },
        }),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to generate speech: ${response.statusText}`);
    }

    return await response.blob();
  } catch (e) {
    voiceStore.error =
      e instanceof Error ? e.message : "Failed to generate speech";
    throw e;
  } finally {
    voiceStore.ttsGenerating = false;
  }
}

/**
 * Play audio preview for a voice
 */
export function playVoicePreview(voice: Voice): void {
  if (voice.preview_url) {
    const audio = new Audio(voice.preview_url);
    audio.play().catch(console.error);
  }
}

/**
 * Get voices by category
 */
export function getVoicesByCategory(category: Voice["category"]): Voice[] {
  return voiceStore.voices.filter((v) => v.category === category);
}

/**
 * Get cloned voices only
 */
export function getClonedVoices(): Voice[] {
  return getVoicesByCategory("cloned");
}
